import React, { useState, useEffect } from 'react';
import { useWtrDispatch, useWtrSelector } from 'redux/hooks';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import classnames from 'classnames';
import root from 'window-or-global';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import { LinkAsButton } from '@johnlewispartnership/wtr-ingredients/ingredients/LinkAsButton/LinkAsButton';
import { Radio } from '@johnlewispartnership/wtr-ingredients/ingredients/Radio/Radio';
import { LabeledControl } from '@johnlewispartnership/wtr-ingredients/ingredients/LabeledControl/LabeledControl';
import { Alert } from '@johnlewispartnership/wtr-ingredients/ingredients/Alert/Alert';
import { getMealPlanner } from 'redux/modules/meal-planner/selectors';
import { MEAL_PLANNER_CLEAR } from 'redux/modules/meal-planner/actions/types';
import { dataLayer } from 'analytics/data-layer';
import { emptyFilter } from 'components/Recipes/MealPlanner/utils/filter';
import styles from './Step-1.scss';
import plannerStyles from './index.scss';

const { history } = root;

const SERVING_SIZE_FILTER_TITLE = 'Serving Size';

export const Step1 = () => {
  const dispatch = useWtrDispatch();

  const { filters } = useWtrSelector(getMealPlanner);
  const servingSizes =
    filters.find(filter => filter.title === SERVING_SIZE_FILTER_TITLE) || emptyFilter;
  const initialSelectedServingSizeName = history?.state?.servingSize || '';
  const [searchParams] = useSearchParams();

  const builder = history?.state?.builder || searchParams.get('builder');

  const [selectedServingSizeName, setSelectedServingSizeName] = useState(
    initialSelectedServingSizeName,
  );
  const [showError, setShowError] = useState(false);

  const nextUrl = builder
    ? `../${builder}?Serving Size=${encodeURIComponent(selectedServingSizeName)}`
    : `../dietary?Serving Size=${encodeURIComponent(selectedServingSizeName)}`;

  useEffect(() => {
    if (selectedServingSizeName) {
      history.replaceState({ servingSize: selectedServingSizeName }, '');
    }
  }, [selectedServingSizeName]);

  const handleNextClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (selectedServingSizeName) {
      dataLayer.push({
        event: 'complete_recipe_serving_size',
        recipe_serving_size: selectedServingSizeName,
      });
    } else {
      event.preventDefault();
      setShowError(true);
    }

    dispatch({ type: MEAL_PLANNER_CLEAR });
  };

  return (
    <>
      <Helmet meta={[{ name: 'robots', content: 'noindex' }]} />
      <div className={classnames(plannerStyles.content, styles.content)}>
        <Typography component="h3" styleAs="paragraphHeading" className={styles.step}>
          Step 1 of 2
        </Typography>
        <Typography component="h1" styleAs="screenHeadingSmall" className={plannerStyles.stepTitle}>
          How many people are you cooking for?
        </Typography>

        {showError && (
          <div className={styles.alertWrapper}>
            <Alert
              type="error"
              title="No option selected"
              aria-live="polite"
              role="alert"
              data-testid="alert"
            >
              Please make a selection before you continue
            </Alert>
          </div>
        )}

        {servingSizes.values.map(servingSize => (
          <LabeledControl
            className={styles.label}
            name="servingSize"
            key={servingSize.name}
            control={<Radio checked={selectedServingSizeName === servingSize.name} />}
            label={servingSize.name}
            onChange={() => {
              setSelectedServingSizeName(servingSize.name);
              setShowError(false);
            }}
          />
        ))}

        <LinkAsButton
          to={nextUrl}
          component={Link}
          /* @ts-expect-error finalising prop */
          theme="finalising"
          disabled={!selectedServingSizeName}
          onClick={handleNextClick}
          className={plannerStyles.nextButton}
        >
          Next
        </LinkAsButton>
      </div>
    </>
  );
};

export default Step1;
